import type { UploadDto, UploadFailureKind, UploadFailureSubkind } from '@flowqa/shared';

export type UploadFailurePresentation = {
  kind: UploadFailureKind;
  label: string;
  subkindLabel: string | null;
  summary: string;
  detail: string | null;
  nextStep: string | null;
};

type UploadFailureInput = Pick<
  UploadDto,
  'error' | 'errorSummary' | 'failureKind' | 'failureSubkind' | 'status'
>;

const fallbackSummaryByKind: Record<UploadFailureKind, string> = {
  duplicate: 'This upload matches an existing results file.',
  limit: 'Execution limit reached. Upgrade to upload more results.',
  parse: 'We could not parse this results file.',
  partial: 'Some results were imported, but one or more files failed.',
  permission: 'This upload needs the right workspace or account access.',
  retry: 'Uploads are temporarily unavailable. Please try again.',
  unknown: 'Upload failed. Please try again.',
  validation: 'Upload failed due to an invalid file.',
};

const labelByKind: Record<UploadFailureKind, string> = {
  duplicate: 'Duplicate upload',
  limit: 'Plan limit',
  parse: 'Parse issue',
  partial: 'Partial import',
  permission: 'Permission issue',
  retry: 'Retry suggested',
  unknown: 'Upload failed',
  validation: 'Validation issue',
};

const uploadFailureKinds = [
  'duplicate',
  'limit',
  'parse',
  'partial',
  'permission',
  'retry',
  'unknown',
  'validation',
] satisfies UploadFailureKind[];

const labelBySubkind: Record<UploadFailureSubkind, string> = {
  checksum_mismatch: 'Checksum mismatch',
  content_type_mismatch: 'Content type mismatch',
  duplicate_upload: 'Duplicate upload',
  email_verification_required: 'Email verification required',
  execution_limit: 'Execution limit',
  file_name: 'File name',
  file_too_large: 'File too large',
  invalid_artifact: 'Invalid artifact',
  invalid_payload: 'Invalid payload',
  parser_error: 'Parser error',
  payload_empty: 'Empty payload',
  project_token_mismatch: 'Project token mismatch',
  queue_unavailable: 'Queue unavailable',
  results_file_parse_failed: 'Results file parse failed',
  unknown: 'Unknown failure',
};

const uploadFailureSubkinds = [
  'checksum_mismatch',
  'content_type_mismatch',
  'duplicate_upload',
  'email_verification_required',
  'execution_limit',
  'file_name',
  'file_too_large',
  'invalid_artifact',
  'invalid_payload',
  'parser_error',
  'payload_empty',
  'project_token_mismatch',
  'queue_unavailable',
  'results_file_parse_failed',
  'unknown',
] satisfies UploadFailureSubkind[];

const nextStepByKind: Record<UploadFailureKind, string | null> = {
  duplicate: 'Check whether the existing upload already contains the results you need.',
  limit: 'Review the workspace plan or reduce the upload volume before trying again.',
  parse: 'Upload a valid JUnit XML, Playwright JSON, or supported coverage report.',
  partial: 'Open the imported results files and re-upload only the files that failed.',
  permission: 'Confirm the upload token, project access, and account verification before retrying.',
  retry: 'Wait a moment, then retry the upload.',
  unknown: null,
  validation: 'Fix the file name, file type, or file contents before uploading again.',
};

export const uploadFailureKindFilterOptions = uploadFailureKinds.map((value) => ({
  value,
  label: labelByKind[value],
}));

export const uploadFailureSubkindFilterOptions = uploadFailureSubkinds.map((value) => ({
  value,
  label: labelBySubkind[value],
}));

const normaliseMessage = (message: string | null | undefined): string =>
  message?.trim().toLowerCase() ?? '';

const resolveFailureKind = (upload: UploadFailureInput): UploadFailureKind => {
  if (upload.failureKind) return upload.failureKind;
  if (upload.status === 'completed_with_errors') return 'partial';

  const message = normaliseMessage(`${upload.errorSummary ?? ''} ${upload.error ?? ''}`);
  if (message.length === 0) return 'unknown';
  if (message.includes('checksum mismatch')) {
    return 'validation';
  }
  if (
    message.includes('duplicate') ||
    message.includes('idempotency') ||
    message.includes('checksum') ||
    message.includes('already exists')
  ) {
    return 'duplicate';
  }
  if (
    message.includes('verify your email') ||
    message.includes('permission') ||
    message.includes('forbidden') ||
    message.includes('not authorised') ||
    message.includes('not authorized') ||
    message.includes('token does not match project')
  ) {
    return 'permission';
  }
  if (
    message.includes('execution limit') ||
    message.includes('limit reached') ||
    message.includes('upgrade to upload') ||
    message.includes('quota')
  ) {
    return 'limit';
  }
  if (
    message.includes('temporarily unavailable') ||
    message.includes('timed out') ||
    message.includes('try again') ||
    message.includes('queue')
  ) {
    return 'retry';
  }
  if (
    message.includes('parse') ||
    message.includes('junit') ||
    message.includes('xml') ||
    message.includes('json')
  ) {
    return 'parse';
  }
  if (
    message.includes('invalid') ||
    message.includes('content type') ||
    message.includes('file extension') ||
    message.includes('file name') ||
    message.includes('too large') ||
    message.includes('empty')
  ) {
    return 'validation';
  }
  return 'unknown';
};

const resolveDetail = (upload: UploadFailureInput, summary: string): string | null => {
  const detail = upload.error?.trim() ?? '';
  if (detail.length === 0) return null;
  return detail === summary ? null : detail;
};

export const resolveUploadFailurePresentation = (
  upload: UploadFailureInput,
): UploadFailurePresentation | null => {
  if (upload.status !== 'failed' && upload.status !== 'completed_with_errors') return null;

  const kind = resolveFailureKind(upload);
  const trimmedSummary = upload.errorSummary?.trim();
  const summary =
    trimmedSummary !== undefined && trimmedSummary.length > 0
      ? trimmedSummary
      : fallbackSummaryByKind[kind];
  return {
    kind,
    label: labelByKind[kind],
    subkindLabel: upload.failureSubkind ? labelBySubkind[upload.failureSubkind] : null,
    summary,
    detail: resolveDetail(upload, summary),
    nextStep: nextStepByKind[kind],
  };
};
